import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { RobotSummaryDto } from '@bhops/shared';
import { robotsApi } from '@/api/robots';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';
import {
  CONTRACT_KIND_SALE,
  CONTRACT_KIND_SUBSCRIPTION,
  CONTRACT_REASON_EMPTY,
} from '@/lib/contract-reason';

// RobotsListPage 의 '확인 필요' 표기와 같은 단어(그 파일의 로컬 상수와 동일).
const CONTRACT_KIND_AMBIGUOUS_LABEL = '확인 필요';

interface Props {
  open: boolean;
  onClose: () => void;
  robot: RobotSummaryDto;
}

const MIN_REASON = 10;

/** 접힌 '구분' 토큰 → 화면 단어. 표기 정본(lib/contract-reason)과 같은 단어만 쓴다. */
function kindLabel(kind: string | null | undefined): string {
  if (kind === 'SALE') return CONTRACT_KIND_SALE;
  if (kind === 'SUBSCRIPTION') return CONTRACT_KIND_SUBSCRIPTION;
  if (kind === 'AMBIGUOUS') return CONTRACT_KIND_AMBIGUOUS_LABEL;
  return CONTRACT_REASON_EMPTY;
}

/**
 * 구분 수동 정정(2026-09-01 오너 지시 — plan retire-rollback-sale-kind-2026-09 §2-5).
 *
 * '구분'(판매/구독)은 예약 계약 줄에서 파생되는 계산값이라 화면에서 고칠 수 없었다.
 * 이 모달이 수동값을 덮거나(판매/구독) 자동으로 되돌린다(자동). 상태 정정과 같은
 * 문법: 사유 10자 필수, 로봇 기록 탭·감사 장부에 전/후와 사유가 남는다.
 * 계약·매출 데이터는 건드리지 않는다 — 화면 표기의 정정일 뿐이다.
 */
export function RobotContractKindModal({ open, onClose, robot }: Props) {
  const toast = useToast();
  const qc = useQueryClient();

  // '' = 자동(수동값 해제). 서버에는 null 로 나간다.
  const [kind, setKind] = useState<string>('');
  const [reason, setReason] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setKind(robot.contractKindSource === 'MANUAL' ? (robot.contractKind ?? '') : '');
      setReason('');
      setError(null);
    }
  }, [open, robot]);

  const correct = useMutation({
    mutationFn: () =>
      robotsApi.correctContractKind(robot.id, {
        kind: kind === 'SALE' || kind === 'SUBSCRIPTION' ? kind : null,
        reason: reason.trim(),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['robot', robot.id] });
      qc.invalidateQueries({ queryKey: ['robots'] });
      qc.invalidateQueries({ queryKey: ['robot-timeline', robot.id] });
      toast.success('구분이 정정되었습니다. 사유가 기록에 남았습니다.', '구분 정정');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  const reasonTooShort = reason.trim().length < MIN_REASON;

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    if (reasonTooShort) {
      setError(`정정 사유를 ${MIN_REASON}자 이상 입력해 주세요.`);
      return;
    }
    correct.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="구분 직접 정정"
      width={480}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="contract-kind-correction-form"
            variant="danger"
            disabled={correct.isPending || reasonTooShort}
          >
            {correct.isPending ? '정정 중…' : '정정 실행'}
          </Button>
        </>
      }
    >
      <form id="contract-kind-correction-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <div
          style={{
            padding: 10,
            marginBottom: 14,
            borderRadius: 'var(--r-card)',
            background: 'var(--bg-sunken)',
            border: '1px solid var(--fg-danger)',
            color: 'var(--fg-danger)',
            fontSize: 13,
            lineHeight: 1.5,
          }}
        >
          구분은 계약 줄에서 자동 계산됩니다. 여기서 고른 값이 자동 계산보다 우선하며, 사유가 기록에
          남습니다. 계약과 매출 데이터는 바뀌지 않습니다.
          <div className="kuun-caption" style={{ marginTop: 4 }}>
            현재 표시: {kindLabel(robot.contractKind)}
            {robot.contractKindSource === 'MANUAL' ? ' (수동)' : ' (자동)'}, 자동 계산값:{' '}
            {kindLabel(robot.contractKindAuto)}
          </div>
        </div>

        <Field
          label="구분"
          htmlFor="contract-kind"
          required
          hint="'자동 계산'을 고르면 직접 정한 값이 풀리고 계약 기준 계산으로 돌아갑니다. 나중에 새 계약에 배정되어도 직접 정한 값은 자동으로 풀립니다."
        >
          <Select id="contract-kind" value={kind} onChange={(e) => setKind(e.target.value)}>
            <option value="">자동 계산</option>
            <option value="SALE">{CONTRACT_KIND_SALE}</option>
            <option value="SUBSCRIPTION">{CONTRACT_KIND_SUBSCRIPTION}</option>
          </Select>
        </Field>

        <Field
          label="정정 사유"
          htmlFor="contract-kind-reason"
          required
          hint={`자동 계산과 왜 다른지 ${MIN_REASON}자 이상 적어 주세요.`}
        >
          <Textarea
            id="contract-kind-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            placeholder="예: 판매로 전환했지만 계약 서류가 아직 등록되지 않아 직접 표기"
          />
        </Field>
      </form>
    </Modal>
  );
}
